//MyAssignmentList.jsx
import React, { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation } from "react-router-dom";
import Loader from "../../components/utils/Loader";
import Tooltip from "../../components/utils/Tooltip";

const MyOtherAssignmentsList = () => {
  const authState = useSelector((state) => state.authReducer);
  const [assignments, setAssignments] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);

  const fetchAssignments = useCallback(() => {
    const config = {
      url: "/otherassignment",
      method: "get",
      headers: { Authorization: authState.token },
    };

    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        // console.log("Response:", response);
        if (response && Array.isArray(response.assignments)) {
          setAssignments(response.assignments);
        } else {
          console.error(
            "Invalid response format: Assignment not found in response"
          );
        }
      })
      .catch((error) => {
        console.error("Error fetching Assignments:", error);
      });
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchAssignments();
  }, [authState.isLoggedIn, fetchAssignments]);

  const handleView = (assignmentId) => {
    setSelectedAssignmentId((prevId) =>
      prevId === assignmentId ? null : assignmentId
    );
  };

  const handleCloseAssignmentDetails = () => {
    setSelectedAssignmentId(null);
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
  };

  const handleAssignmentClick = (assignmentId) => {
    setSelectedAssignmentId(assignmentId);
  };

  const handleAssignmentShare = (assignmentId) => {
    setSelectedAssignmentId(assignmentId);
  };

  const handleAssignmentSave = (assignmentId) => {
    setSelectedAssignmentId(assignmentId);
  };

  const handleAssignmentPrint = (assignmentId) => {
    setSelectedAssignmentId(assignmentId);
  };

  const formatDate = (date) => {
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, "0");
    const month = (jsDate.getMonth() + 1).toString().padStart(2, "0");
    const year = jsDate.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const sortedAssignments = [...assignments].sort(
    (a, b) => new Date(b.dateCreated) - new Date(a.dateCreated)
  );

  return (
    <>
      <div className="my-4 mx-auto max-w-4xl text-white p-4 [&_p]:text-white">
        {assignments.length > 0 && (
          <div className="bg-orange-500 text-white rounded-lg py-2 px-4 shadow-md">
            <h2 className="text-xl font-bold">
              Your Assignments ({assignments.length})
            </h2>
          </div>
        )}
        <div className="border border-gray-300 mt-4 rounded-lg overflow-hidden shadow-lg">
          {loading ? (
            <div className="flex justify-center items-center p-6">
              <Loader />
            </div>
          ) : (
            <div className="p-4">
              {assignments.length === 0 ? (
                <div className="flex items-center justify-center text-yellow-300">
                  No assignment record found!
                </div>
              ) : (
                
                sortedAssignments.map((assignment, index) => (
                  <div
                    key={assignment._id}
                    className="bg-white my-4 p-4 text-gray-800 rounded-lg shadow-md"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-lg">
                        #{index + 1}. {assignment.title}
                      </span>
                      <div className="flex space-x-2">
                        <Tooltip text="View details">
                          <span
                            className="text-blue-500 cursor-pointer hover:text-blue-700 transition"
                            onClick={() => handleView(assignment._id)}
                          >
                            <i className="fa-solid fa-eye"></i>
                          </span>
                        </Tooltip>
                        <Tooltip text="Edit this record" position="top">
                          <Link
                            to={`/otherAssignments/${assignment._id}`}
                            className="text-green-600 cursor-pointer hover:text-green-800 transition"
                          >
                            <i className="fa-solid fa-pen"></i>
                          </Link>
                        </Tooltip>
                      </div>
                    </div>
                    {selectedAssignmentId === assignment._id && (
                      <div className="bg-gray-100 text-gray-800 mt-4 p-4 rounded-lg shadow-sm [&_p]:text-white">
                        <h2 className="text-blue-600 text-lg font-semibold">
                          {assignment.topicType} : {assignment.title}
                        </h2>
                        <p className="mt-2">
                          <strong>Topic Type:</strong> {assignment.topicType}
                        </p>
                        <p>
                          <strong>Date Created:</strong> {formatDate(assignment.dateCreated)}
                        </p>
                        <p>
                          <strong>Title / Topic:</strong> {assignment.title}
                        </p>
                        <p>
                          <strong>Description:</strong> {assignment.statement}
                        </p>
                        {assignment.details1 && (
                          <p>
                            <strong>Details 1:</strong> {assignment.details1}
                          </p>
                        )}
                        {assignment.details2 && (
                          <p>
                            <strong>Details 2:</strong> {assignment.details2}
                          </p>
                        )}
                        {assignment.details3 && (
                          <p>
                            <strong>Details 3:</strong> {assignment.details3}
                          </p>
                        )}
                        {assignment.details4 && (
                          <p>
                            <strong>Details 4:</strong> {assignment.details4}
                          </p>
                        )}
                        {assignment.details5 && (
                          <p>
                            <strong>Details 5:</strong> {assignment.details5}
                          </p>
                        )}
                        <p>
                          <strong>Outcome / Learnings:</strong> {assignment.outcome}
                        </p>
                        {assignment.summary && (
                          <p>
                            <strong>Summary:</strong> {assignment.summary}
                          </p>
                        )}
                        {assignment.remarks && (
                          <p>
                            <strong>Remarks:</strong> {assignment.remarks}
                          </p>
                        )}
                        <div className="flex space-x-3 mt-4">
                          <Tooltip text="Share" position="top">
                            <span
                              className="text-green-500 cursor-pointer hover:text-green-700 transition"
                              onClick={() => handleAssignmentShare(assignment._id)}
                            >
                              <i className="fa-solid fa-share"></i>
                              </span>
                        </Tooltip>
                        <Tooltip text="Save" position="top">
                          <span
                            className="text-blue-500 cursor-pointer hover:text-blue-700 transition"
                            onClick={() => handleAssignmentSave(assignment._id)}
                          >
                            <i className="fa-solid fa-save"></i>
                          </span>
                        </Tooltip>
                        <Tooltip text="Print" position="top">
                          <span
                            className="text-orange-500 cursor-pointer hover:text-orange-700 transition"
                            onClick={() => handleAssignmentPrint(assignment._id)}
                          >
                            <i className="fa-solid fa-print"></i>
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  </>
);
              
  
};

export default MyOtherAssignmentsList;
